





























































import mixins from 'vue-typed-mixins';
import QuestionTypeMixin from './QuestionTypeMixin';
import SurveyNodeUtils from '@/utils/SurveyNodeUtils';
import SurveyUtils, { Question } from '@/utils/SurveyUtils';
import { ReportNode, QuestionData, ReportContext } from '@/utils/ReportUtils';
import { SubOrganization } from '@/utils/SubOrganizationUtils';
import { TableFormatOptions } from '../options';

export default mixins(QuestionTypeMixin).extend({
  components: {
    // https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    // this is needed to fix a circular dependancy on Question.vue
    QuestionVue: () =>
      import('@/components/survey/report/nodes/Question.vue') as any,
  },

  computed: {
    internalContext(): ReportContext {
      return {
        ...this.context,
        // nodes: this.nodes, // commented out so that it uses all the nodes for conditional questions
        mode: (this.context.mode || [])?.concat('table'),
      };
    },

    layout(): TableFormatOptions['layout'] | undefined {
      return this.value.item.options.layout
        ?.map((item: any) => ({
          ...item,
          question: this.context.questions?.find(
            (question) => question.id === item.question.id
          ),
        }))
        ?.filter((layout: any) => !!layout.question);
    },

    headers(): any {
      return [
        {
          text:
            this.$translate(this.context.survey?.languageobjects?.suborg) ||
            this.$tc('model.survey.suborg'),
          value: 'suborg.name',
        },
        ...(this.layout || []).map((item) => {
          return {
            text: this.$translate(item.header),
            value: item.question?.id,
          };
        }),
      ];
    },

    valid(): boolean {
      return this.nodes?.every((item) => item?.data.valid) || false;
    },

    dirty(): boolean {
      return this.nodes?.some((node) => node.data.dirty) || false;
    },
  },

  data(): {
    nodes?: ReportNode<Question, QuestionData>[];
    suborganization?: SubOrganization;
    items?: {
      suborg: SubOrganization;
      answers: { question: Question; answer: any }[];
    }[];
  } {
    return {
      nodes: undefined,
      suborganization: undefined,
      items: undefined,
    };
  },

  async mounted() {
    this.nodes = this.layout?.map(
      (item) =>
        SurveyNodeUtils.generateNode(item.question) as ReportNode<
          Question,
          QuestionData
        >
    );

    if (
      this.context.surveyParticipation &&
      this.context.suborganizations &&
      this.context.suborganizations.length > 0
    ) {
      try {
        this.loadTable();
      } catch (error) {
        this.$alert.show('mount', {
          type: 'error',
          messages: this.$handleAPIError(error),
        });
      }
    }
  },

  watch: {
    items() {
      this.nodes = this.layout?.map(
        (item) =>
          SurveyNodeUtils.generateNode(item.question) as ReportNode<
            Question,
            QuestionData
          >
      );
    },

    '$i18n.locale'() {
      this.loadTable(); // reload when language changes
    },

    dirty() {
      if (
        !(
          this.context.suborganizations &&
          this.context.suborganizations.length > 0
        )
      ) {
        this.value.data.dirty = this.dirty;
      } else {
        this.value.data.dirty = false;
      }
    },
  },

  methods: {
    async before() {
      await this.save();
    },

    open(suborganization: SubOrganization) {
      this.suborganization = suborganization;
    },

    close() {
      this.suborganization = undefined;
    },

    async save() {
      try {
        (this.$refs.alert as any)?.hide('save');
        await Promise.all(
          (this.$refs.questions as any)
            .filter((component: any) => typeof component.save === 'function')
            .map((component: any) => component.save())
        );

        this.loadTable(this.suborganization);

        this.$toast.push({
          type: 'success',
          text: this.$languageobj('validation.success.default', {
            values: [this.$languageobj('model.survey.label')],
          }),
        });
        this.close();
      } catch (error) {
        (this.$refs.alert as any)?.show('save', {
          type: 'error',
          messages: this.$handleAPIError(error),
        });
      }
    },

    async loadTable(suborganization?: SubOrganization) {
      if (
        this.context.surveyParticipation &&
        this.context.suborganizations &&
        this.context.suborganizations.length > 0
      ) {
        try {
          // hold items in temp variable to show loading bar
          const items = this.items;
          this.items = undefined;

          this.$alert.hide('loadTable');
          const resp = await SurveyUtils.api.question.listTableQuestions({
            surveyparticipation: this.context.surveyParticipation,
            suborganization: suborganization,
            questions: (this.layout?.map((item) => item.question) || [])?.map(
              (question) => ({
                id: question?.id,
              })
            ),
          });

          if (suborganization && items) {
            const index = items.findIndex(
              (item) => item.suborg.id === suborganization.id
            );
            if (index >= 0) {
              items[index] = resp[0];
            } else {
              items.push(resp[0]);
            }
            this.items = items;
          } else {
            this.items = resp;
          }
        } catch (error) {
          this.$alert.show('loadTable', {
            type: 'error',
            messages: this.$handleAPIError(error),
          });
        }
      }
    },
  },
});
